import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { Filter } from '@vk-hr-tek/core/filter';

import { ThunkExtra } from '@app/store';
import { AttorneyListResponse, UsersService } from '@app/gen/attorneys';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';

import { AttorneysListService } from '../../../services';
import { SettingsState, SettingsWithRootState } from '../../settings.state';
import { GetAttorneysListDto } from '../../../dto';

const setAttorneysListFilters = createAction<Filter[] | null>(
  'settings/setAttorneysListFilters',
);

export const setSelectedAttorneys = createAction<string[]>(
  'settings/setSelectedAttorneys',
);

export const getAttorneysList = createAsyncThunk<
  AttorneyListResponse,
  GetAttorneysListDto,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAttorneysList',
  async (
    getAttorneysListDto,
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const state = getState().settings;

      const attorneyService = inject(AttorneysListService);

      const service = inject(UsersService);

      let filters = state.attorneys.filters;
      const companies = state.attorneysCompanies.items;

      if (
        !companies ||
        !companies.find(
          (company) => company.company.id === getAttorneysListDto.company_id,
        )
      ) {
        const error = new AppError('client', {
          code: 404,
          message: 'Компания не найдена',
        });

        error.title = 'Компания не найдена';

        throw error;
      }

      if (!filters) {
        const filtersResponse = await service.attorneyListAvailableFilters();
        filters = filtersResponse.filters;
        dispatch(setAttorneysListFilters(filters));
      }

      const result = await attorneyService.getAttorneysList(
        getAttorneysListDto,
        filters,
      );

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsAttorneysListReducer = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getAttorneysList.pending, (state) => {
    state.attorneys.status = 'loading';
  });
  builder.addCase(getAttorneysList.fulfilled, (state, { payload }) => {
    state.attorneys.status = 'complete';
    state.attorneys.attorneys = payload.attorneys;
    state.attorneys.total = payload.total || 0;
  });
  builder.addCase(
    getAttorneysList.rejected,
    (state, { payload, error, meta }) => {
      if (!meta.aborted) {
        state.attorneys.status = 'failed';
        state.attorneys.error =
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError);
      }
    },
  );

  builder.addCase(setAttorneysListFilters, (state, action) => {
    state.attorneys.filters = action.payload;
  });

  builder.addCase(setSelectedAttorneys, (state, action) => {
    state.attorneys.selected = action.payload;
  });
};
